/**
 * useSendTutorMessageAdapter — vapor upload + send-message-to-tutor for the DS modal.
 *
 * Owns message text + media draft, bridges `<MediaPicker>` via `useMediaPickerAdapter`, and
 * submits through the same vapor → `authService.sendMessageToTutor` batch as legacy.
 */
import { useCallback, useEffect, useMemo, useState } from "react"
import moment from "moment"

import { useRequestsBatch } from "../../../../common/hooks/useRequestsBatch"
import { formIds } from "../../../../data/constants/formConstants"
import { authService } from "../../../../data/services/authService"
import { type MediaPickerProps } from "../../../../design-system/organisms/MediaPicker"
import { useGesture } from "./gestureTracking"
import { useMediaPickerAdapter } from "./useMediaPickerAdapter"

type MediaImage = { uri: string; width?: number; height?: number }

export type UseSendTutorMessageAdapterArgs = {
    studentId: string | number | null | undefined
    tutorId: string | number | null | undefined
    tutorDisplayName: string
    visible: boolean
    onClose: () => void
    onSuccess: () => void
    hidden?: boolean
    showParentHint?: boolean
    /** Le retour vers « Quel tuteur ? » — seulement quand le composeur en est la 2ᵉ étape. */
    onBack?: () => void
}

export type SendTutorMessageAdapterResult = {
    visible: boolean
    onClose: () => void
    tutorDisplayName: string
    message: string
    onMessageChange: (next: string) => void
    mediaProps: MediaPickerProps
    onSubmit: () => void
    submitLoading: boolean
    submitDisabled: boolean
    showParentHint: boolean
    hidden: boolean
    errorMessage: string | null
    onBack?: () => void
}

export function useSendTutorMessageAdapter(args: UseSendTutorMessageAdapterArgs): SendTutorMessageAdapterResult {
    const {
        studentId,
        tutorId,
        tutorDisplayName,
        visible,
        onClose,
        onSuccess,
        hidden = false,
        showParentHint = false,
        onBack,
    } = args

    const formId = formIds.SEND_TUTOR_MESSAGE
    const [message, setMessage] = useState("")
    const [images, setImages] = useState<MediaImage[]>([])
    const [pdf, setPdf] = useState<string | null>(null)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const gesture = useGesture("message_tutor")

    useEffect(() => {
        if (visible) gesture.opened()
        else {
            setMessage("")
            setImages([])
            setPdf(null)
            setErrorMessage(null)
        }
    }, [visible, gesture])

    const mediaProps = useMediaPickerAdapter({
        images,
        setImages,
        pdf,
        setPdf,
        formId,
        multiple: true,
        enableExternalLink: false,
        enableDragAndDrop: true,
        buttonLabel: "Photo ou document",
        addMoreButtonLabel: "Ajouter",
    })

    const filesToUpload = useMemo(() => {
        const date = moment().format("YYYY-MM-DD")
        const files: { uri: string; name: string; type: string }[] = []
        for (const img of images) {
            files.push({
                uri: img.uri,
                name: `image_${date}.jpg`,
                type: "image/jpeg",
            })
        }
        if (pdf) {
            files.push({ uri: pdf, name: `document_${date}.pdf`, type: "application/pdf" })
        }
        return files
    }, [images, pdf])

    const [, submitted, startRequests] = useRequestsBatch(
        [],
        filesToUpload,
        formId,
        {
            service: authService.sendMessageToTutor,
            data: {
                subscriptionUserID: studentId,
                tutorId,
                message,
            },
            tmpFilesKey: "message_attachments",
            tmpFilesFormatter: (attachmentTmp: string, index: number) => ({
                url: attachmentTmp,
                name: filesToUpload[index]?.name,
            }),
            additionalData: [],
            onSuccessBeforeNext: () => {
                gesture.succeeded()
                setErrorMessage(null)
                onSuccess()
                onClose()
            },
            onError: (error?: unknown) => {
                gesture.failed(error)
                setErrorMessage("Erreur lors de l'envoi du message. Veuillez réessayer.")
            },
        },
        [],
        true
    ) as [number, boolean, () => void]

    const submitDisabled =
        (message.trim() === "" && images.length === 0 && pdf == null) || studentId == null || tutorId == null

    const onSubmit = useCallback(() => {
        if (submitDisabled || submitted) return
        setErrorMessage(null)
        startRequests()
        gesture.submitted()
    }, [submitDisabled, submitted, startRequests, gesture])

    return {
        visible,
        onClose,
        tutorDisplayName,
        message,
        onMessageChange: setMessage,
        mediaProps,
        onSubmit,
        submitLoading: !!submitted,
        submitDisabled,
        showParentHint,
        hidden,
        errorMessage,
        onBack,
    }
}
